import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Steps } from "primereact/steps";
import { FormProvider, useForm } from "react-hook-form";
import {
  savePreloanForm,
  fetchPreloanForm,
} from "../../../../services/preloanFormApi";
import StepTenant from "./PreLoanSteps/StepTenant";
import StepReferences from "./PreLoanSteps/StepReferences";
import StepContract from "./PreLoanSteps/StepContract";
import StepDownPayment from "./PreLoanSteps/StepDownPayment";
import StepRent from "./PreLoanSteps/StepRent";
import StepVehicle from "./PreLoanSteps/StepVehicle";
import StepEquity from "./PreLoanSteps/StepEquity";
import StepLegalRep from "./PreLoanSteps/StepLegalRep";
import StepGuarantor from "./PreLoanSteps/StepGuarantor";
import StepTenantPM from "./PreLoanSteps/StepTenantPM";
import PDFModal from "./PDFModal";
import axios from "axios";
import { url } from "../../../../env";
import { parseDateString } from "./preloanCache";
import { ResponsiveDialogProvider } from "../../../../Context/ResponsiveDialogContext";
import { bpoints, getMinWidth } from "../../../../Utils/breakpoints";
import { styles } from "./PreLoan.css";
import { Loader } from "../../../../Components/Loader/Loader";
import { Tooltip } from "primereact/tooltip";

// Definición de TODOS los campos por paso (quitar los que no son requeridos)
export const REQUIRED_FIELDS = {
  tenant: [
    "tenant.nombreArrendatario",
    "tenant.numeroContrato",
    "tenant.domicilioParticular",
    "tenant.domicilioFiscal",
    "tenant.rfc",
    "tenant.telefono",
    "tenant.correo",
    "tenant.actividadEconomica",
    "tenant.entidadFederativa",
    "tenant.curp",
    "tenant.opinionCumplimiento",
    "tenant.estatus",
    "tenant.idCIF",
    "tenant.regimenFiscal",
    "tenant.fechaNacimiento",
    "tenant.identificacion",
    "tenant.claveElector",
    "tenant.nss",
    "tenant.emisionIdentificacion",
    "tenant.vigenciaIdentificacion",
  ],
  tenantPM: [
    "tenant.nombreArrendamiento",
    "tenant.numeroContrato",
    "tenant.domicilioOperativo",
    "tenant.domicilioFiscal",
    "tenant.rfc",
    "tenant.telefono",
    "tenant.correo",
    "tenant.fechaInscripcion",
    "tenant.fechaConstitucion",
    "tenant.fechaRegistro",
    "tenant.actividadEconomica",
    "tenant.numeroEscritura",
    "tenant.libro",
    "tenant.fedatarioNumero",
    "tenant.entidadFederativa",
    "tenant.fedatarioNombre",
    "tenant.opinionCumplimiento",
    "tenant.estatus",
    "tenant.idCIF",
    "tenant.regimenFiscal",
  ],
  referencias: [
    "referencias.nombre", // para cada índice [0], [1], [2]
    "referencias.telefono", // para cada índice [0], [1], [2]
    "referencias.parentesco", // para cada índice [0], [1], [2]
  ],
  equity: [
    "equity.accionista", // para cada fila dinámica
    /*         'equity.valor', // para cada fila dinámica */
    "equity.porcentaje", // para cada fila dinámica
  ],
  legalRep: [
    "legalRep.nombre",
    "legalRep.fechaNacimiento",
    "legalRep.entidadNacimiento",
    "legalRep.identificacion",
    "legalRep.claveElector",
    "legalRep.emision",
    "legalRep.vigencia",
    /*    'legalRep.ocr', */
    "legalRep.vencimiento",
    "legalRep.nss",
  ],
  guarantor: [
    "guarantor.nombre",
    "guarantor.fechaNacimiento",
    "guarantor.curp",
    "guarantor.direccion",
    "guarantor.rfc",
    "guarantor.identificacion",
    "guarantor.claveElector",
    "guarantor.emision",
    "guarantor.vigencia",
    "guarantor.nss",
  ],
  contrato: [
    "contrato.vigenciaMeses",
    "contrato.fechaPrimerPago",
    "contrato.tasaMoratoriaDiaria",
  ],
  pagoInicial: [
    "pagoInicial.enganche",
    "pagoInicial.comisionApertura",
    "pagoInicial.recuperacionGastos",
    "pagoInicial.depositoGarantia",
    "pagoInicial.totalSinIva",
    "pagoInicial.iva",
    "pagoInicial.totalConIva",
  ],
  renta: [
    "renta.sinIva",
    "renta.iva",
    "renta.totalConIva",
    "renta.numeroPagos",
  ],
  auto: [
    "auto.marca",
    "auto.modelo",
    "auto.version",
    "auto.tipoMotor",
    "auto.tipoAuto",
    "auto.accesoriosConIva",
    "auto.estadoPlaca",
    "auto.agencia",
    "auto.numeroSerie",
    "auto.motor",
    "auto.valorFactura",
    "auto.satProductService",
    "auto.satUnit",
    /*   "auto.serialNumber", */
  ],
};

// Función utilitaria para obtener reglas de validación
export const getValidationRules = (fieldName, stepKey) => {
  const requiredFields = REQUIRED_FIELDS[stepKey] || [];
  const isRequired = requiredFields.some(
    (requiredField) =>
      fieldName === requiredField || fieldName.startsWith(requiredField)
  );

  if (isRequired) {
    const rules = {
      required: `${getFieldLabel(fieldName)} es requerido`,
    };
    return rules;
  }

  return {};
};

// Función para obtener el label amigable del campo
const getFieldLabel = (fieldName) => {
  const fieldLabels = {
    // Tenant fields
    "tenant.nombreArrendatario": "Nombre del arrendatario",
    "tenant.numeroContrato": "Número de contrato",
    "tenant.domicilioParticular": "Domicilio particular",
    "tenant.domicilioFiscal": "Domicilio fiscal",
    "tenant.rfc": "RFC",
    "tenant.telefono": "Teléfono",
    "tenant.correo": "Correo",
    "tenant.actividadEconomica": "Actividad económica",
    "tenant.entidadFederativa": "Entidad federativa",
    "tenant.curp": "CURP",
    "tenant.opinionCumplimiento": "Opinión de cumplimiento",
    "tenant.estatus": "Estatus",
    "tenant.idCIF": "ID CIF",
    "tenant.regimenFiscal": "Régimen fiscal",
    "tenant.fechaNacimiento": "Fecha de nacimiento",
    "tenant.identificacion": "Identificación",
    "tenant.claveElector": "Clave de elector",
    "tenant.nss": "NSS",
    "tenant.emisionIdentificacion": "Emisión de identificación",
    "tenant.vigenciaIdentificacion": "Vigencia de identificación",

    // TenantPM fields
    "tenant.nombreArrendamiento": "Nombre del arrendamiento",
    "tenant.domicilioOperativo": "Domicilio operativo",
    "tenant.fechaInscripcion": "Fecha de inscripción",
    "tenant.fechaConstitucion": "Fecha de constitución",
    "tenant.fechaRegistro": "Fecha de registro",
    "tenant.numeroEscritura": "Número de escritura",
    "tenant.libro": "Libro",
    "tenant.fedatarioNumero": "Fedatario número",
    "tenant.fedatarioNombre": "Fedatario nombre",

    // References fields
    "referencias.nombre": "Nombre",
    "referencias.telefono": "Teléfono",
    "referencias.parentesco": "Parentesco",

    // Equity fields (excluyendo valor que está comentado)
    "equity.accionista": "Accionista",
    "equity.porcentaje": "Porcentaje",

    // LegalRep fields (excluyendo ocr que está comentado)
    "legalRep.nombre": "Nombre",
    "legalRep.fechaNacimiento": "Fecha de nacimiento",
    "legalRep.entidadNacimiento": "Entidad de nacimiento",
    "legalRep.identificacion": "Identificación",
    "legalRep.claveElector": "Clave de elector",
    "legalRep.emision": "Emisión",
    "legalRep.vigencia": "Vigencia",
    "legalRep.vencimiento": "Vencimiento",
    "legalRep.nss": "NSS",

    // Guarantor fields
    "guarantor.nombre": "Nombre",
    "guarantor.fechaNacimiento": "Fecha de nacimiento",
    "guarantor.curp": "CURP",
    "guarantor.direccion": "Dirección",
    "guarantor.rfc": "RFC",
    "guarantor.identificacion": "Identificación",
    "guarantor.claveElector": "Clave de elector",
    "guarantor.emision": "Emisión",
    "guarantor.vigencia": "Vigencia",
    "guarantor.nss": "NSS",

    // Contract fields
    "contrato.vigenciaMeses": "Vigencia en meses",
    "contrato.fechaPrimerPago": "Fecha del primer pago",
    "contrato.tasaMoratoriaDiaria": "Tasa moratoria diaria",

    // DownPayment fields
    "pagoInicial.enganche": "Enganche",
    "pagoInicial.comisionApertura": "Comisión por apertura",
    "pagoInicial.recuperacionGastos": "Recuperación de gastos",
    "pagoInicial.depositoGarantia": "Depósito en garantía",
    "pagoInicial.totalSinIva": "Total sin IVA",
    "pagoInicial.iva": "IVA",
    "pagoInicial.totalConIva": "Total con IVA",

    // Rent fields
    "renta.sinIva": "Sin IVA",
    "renta.iva": "IVA",
    "renta.totalConIva": "Total con IVA",
    "renta.numeroPagos": "Número de pagos",

    // Auto fields
    "auto.marca": "Marca",
    "auto.modelo": "Modelo",
    "auto.version": "Versión",
    "auto.tipoMotor": "Tipo de motor",
    "auto.tipoAuto": "Tipo de auto",
    "auto.accesoriosConIva": "Accesorios con IVA",
    "auto.estadoPlaca": "Estado de placa",
    "auto.agencia": "Agencia",
    "auto.numeroSerie": "Número de serie",
    "auto.motor": "Motor",
    "auto.valorFactura": "Valor factura",
    "auto.satProductService": "Clave SAT producto/servicio",
    "auto.satUnit": "Unidad SAT",
    "auto.serialNumber": "Numero de serie",
  };

  return fieldLabels[fieldName] || fieldName.split(".").pop();
};

// Función para verificar si un campo es requerido
export const isFieldRequired = (fieldName, stepKey) => {
  const requiredFields = REQUIRED_FIELDS[stepKey] || [];

  const isRequired = requiredFields.some(
    (requiredField) =>
      fieldName === requiredField || fieldName.startsWith(requiredField)
  );

  return isRequired;
};

// Funciones auxiliares para manejo de campos sucios (movidas fuera del componente para evitar re-renders)
const pickDirty = (values, dirty) => {
  if (!dirty || typeof dirty !== "object") return undefined;
  if (Array.isArray(dirty)) {
    const out = dirty
      .map((d, i) => pickDirty(values?.[i], d))
      .filter((v) => v !== undefined);
    return out.length ? out : undefined;
  }
  const out = {};
  for (const k of Object.keys(dirty)) {
    if (dirty[k] === true) {
      out[k] = values?.[k];
    } else if (typeof dirty[k] === "object") {
      const nested = pickDirty(values?.[k], dirty[k]);
      if (
        nested !== undefined &&
        (typeof nested !== "object" || (nested && Object.keys(nested).length))
      ) {
        out[k] = nested;
      }
    }
  }
  return Object.keys(out).length ? out : undefined;
};

const collectFieldPaths = (obj, base = "") => {
  if (obj === null || obj === undefined) return [];
  if (obj instanceof Date) return base ? [base] : [];
  const paths = [];
  if (Array.isArray(obj)) {
    obj.forEach((item, idx) => {
      const nextBase = base ? `${base}.${idx}` : String(idx);
      if (
        item !== null &&
        typeof item === "object" &&
        !(item instanceof Date)
      ) {
        const inner = collectFieldPaths(item, nextBase);
        paths.push(...(inner.length ? inner : [nextBase]));
      } else {
        paths.push(nextBase);
      }
    });
    return paths;
  }
  if (typeof obj === "object") {
    const keys = Object.keys(obj);
    if (keys.length === 0) return base ? [base] : [];
    keys.forEach((k) => {
      const v = obj[k];
      const nextBase = base ? `${base}.${k}` : k;
      if (v !== null && typeof v === "object" && !(v instanceof Date)) {
        const inner = collectFieldPaths(v, nextBase);
        paths.push(...(inner.length ? inner : [nextBase]));
      } else {
        paths.push(nextBase);
      }
    });
    return paths;
  }
  return base ? [base] : [];
};

const getValueAtPath = (obj, path) => {
  if (!path) return undefined;
  const segments = path.split(".");
  let cur = obj;
  for (const seg of segments) {
    const key = /^\d+$/.test(seg) ? Number(seg) : seg;
    cur = cur?.[key];
    if (cur === undefined) break;
  }
  return cur;
};

// A simple custom stepper component as requested by the user's placeholder
const CustomStepper = ({
  steps,
  activeIndex,
  onStepClick,
  validateBeforeNavigate,
  saveStatus,
  completedSteps = {},
  handleNext,
}) => {
  const handleStepClick = async (index) => {
    // Hacia atrás: navegar libre, pero si está finalizado intentar guardar
    // los cambios del paso actual antes de irnos (best-effort, sin bloquear
    // si la validación falla — el usuario está navegando, no abandonando).
    if (index < activeIndex) {
      if (saveStatus === "finalized" && steps[activeIndex]) {
        try {
          await handleNext({ preventDefault: () => {} }, steps[activeIndex].key);
        } catch (_) {
          // No bloquear navegación hacia atrás si el save/validación falla.
        }
      }
      onStepClick(index);
      return;
    }

    // Hacia adelante (incluido el caso finalizado): validar + guardar
    // como flujo normal. validateBeforeNavigate ya llama a savePreloanForm.
    if (index > activeIndex) {
      const isValid = await validateBeforeNavigate(activeIndex);
      if (isValid) {
        onStepClick(index);
      }
    } else {
      // Mismo paso: navegar.
      onStepClick(index);
    }
  };

  const items = steps.map((step, index) => {
    const isCompleted = completedSteps[step.key];
    const isCurrent = index === activeIndex;
    const isError = isCurrent && saveStatus === "error";

    return {
      label: ".",
      template: (item) => {
        let backgroundColor = "var(--surface-300)"; // Default gray
        let color = "var(--text-color-secondary)";

        if (isError) {
          backgroundColor = "var(--red-500)";
          color = "#ffffff";
        } else if (isCompleted) {
          backgroundColor = "var(--green-500)";
          color = "#ffffff";
        } else if (isCurrent) {
          backgroundColor = "var(--orange-500)";
          color = "#ffffff";
        }

        return (
          <span
            onClick={() => handleStepClick(index)}
            className="p-steps-number"
            style={{
              backgroundColor,
              color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2rem",
              height: "2rem",
              borderRadius: "50%",
              fontSize: "1.143rem",
              fontWeight: "600",
              transition: "background-color 0.2s, color 0.2s, box-shadow 0.2s",
              cursor: "pointer"
            }}
          >
            {index + 1}
          </span>
        );
      },
    };
  });

  return <Steps model={items} activeIndex={activeIndex} readOnly={false} />;
};

const pfSteps = [
  { key: "tenant", label: "Datos del arrendamiento" },
  { key: "referencias", label: "Referencias" },
  { key: "contrato", label: "Características del contrato" },
  { key: "pagoInicial", label: "Características del pago inicial" },
  { key: "renta", label: "Características de la renta" },
  { key: "auto", label: "Características del auto" },
];

const pmSteps = [
  { key: "tenant", label: "Datos del arrendamiento" },
  { key: "equity", label: "Cuadro Accionario actual" },
  { key: "legalRep", label: "Datos del Representante legal" },
  { key: "guarantor", label: "Datos del Obligado Solidario" },
  { key: "contrato", label: "Características del contrato" },
  { key: "pagoInicial", label: "Características del pago inicial" },
  { key: "renta", label: "Características de la renta" },
  { key: "auto", label: "Características del auto" },
];

export default function PreLoan({
  persona,
  canContinue,
  setVisible,
  visible,
  toast,
  categoriasFaltantes,
  onContractFinalized,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({}); // { tenant: true, auto: false, ... }
  const abortRef = useRef(null);
  const lastSavedValuesRef = useRef(null);
  const lastFetchedPersonaIdRef = useRef(null); // Para evitar peticiones duplicadas
  const enableAutosave = false; // Cambiar a true si se quiere autosave
  const debounceTimerRef = useRef(null);

  const stepItems = persona.tipo_persona === "PF" ? pfSteps : pmSteps;

  // Default values builder (usa data del backend si está disponible)
  const buildDefaultValues = (dataFromBackend) => {
    if (!dataFromBackend) {
      return;
    }
    if (persona.tipo_persona === "PF") {
      const objectPF = {
        tenant: {
          nombreArrendatario:
            dataFromBackend?.tenant?.nombreArrendatario ?? persona?.name ?? "",
          numeroContrato:
            dataFromBackend?.tenant?.numeroContrato != null
              ? String(dataFromBackend?.tenant?.numeroContrato)
              : "",
          domicilioParticular:
            dataFromBackend?.tenant?.domicilioParticular ?? "",
          domicilioFiscal: dataFromBackend?.tenant?.domicilioFiscal ?? "",
          rfc: dataFromBackend?.tenant?.rfc ?? "",
          telefono: dataFromBackend?.tenant?.telefono ?? "",
          correo: dataFromBackend?.tenant?.correo ?? "",
          actividadEconomica: dataFromBackend?.tenant?.actividadEconomica ?? "",
          entidadFederativa:
            dataFromBackend?.tenant?.entidadFederativa ??
            persona?.state_plate_id ??
            "",
          curp: dataFromBackend?.tenant?.curp ?? "",
          opinionCumplimiento:
            dataFromBackend?.tenant?.opinionCumplimiento ?? "",
          estatus: dataFromBackend?.tenant?.estatus ?? "",
          idCIF: dataFromBackend?.tenant?.idCIF ?? "",
          regimenFiscal:
            dataFromBackend?.tenant?.regimenFiscal ?? persona?.regimen_id ?? "",
          fechaNacimiento:
            parseDateString(dataFromBackend?.tenant?.fechaNacimiento) ?? null,
          identificacion: dataFromBackend?.tenant?.identificacion ?? "",
          claveElector: dataFromBackend?.tenant?.claveElector ?? "",
          emisionIdentificacion:
            parseDateString(dataFromBackend?.tenant?.emisionIdentificacion) ??
            null,
          vigenciaIdentificacion:
            parseDateString(dataFromBackend?.tenant?.vigenciaIdentificacion) ??
            null,
          nss: dataFromBackend?.tenant?.nss ?? "",
        },
        referencias: [
          {
            nombre: dataFromBackend?.referencias?.[0]?.nombre ?? "",
            telefono: dataFromBackend?.referencias?.[0]?.telefono ?? "",
            parentesco: dataFromBackend?.referencias?.[0]?.parentesco ?? "",
          },
          {
            nombre: dataFromBackend?.referencias?.[1]?.nombre ?? "",
            telefono: dataFromBackend?.referencias?.[1]?.telefono ?? "",
            parentesco: dataFromBackend?.referencias?.[1]?.parentesco ?? "",
          },
          {
            nombre: dataFromBackend?.referencias?.[2]?.nombre ?? "",
            telefono: dataFromBackend?.referencias?.[2]?.telefono ?? "",
            parentesco: dataFromBackend?.referencias?.[2]?.parentesco ?? "",
          },
        ],
        contrato: {
          vigenciaMeses:
            dataFromBackend?.contrato?.vigenciaMeses ??
            dataFromBackend?.pagoInicial?.months ??
            undefined,
          fechaPrimerPago:
            parseDateString(dataFromBackend?.contrato?.fechaPrimerPago) ??
            undefined,
          tasaMoratoriaDiaria:
            dataFromBackend?.contrato?.tasaMoratoriaDiaria ?? undefined,
        },
        pagoInicial: {
          enganche:
            dataFromBackend?.pagoInicial?.enganche ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.enganche ??
            "",
          comisionApertura:
            dataFromBackend?.pagoInicial?.comisionApertura ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.comisionApertura ??
            "",
          recuperacionGastos:
            dataFromBackend?.pagoInicial?.recuperacionGastos ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.gastosOriginacion ??
            "",
          depositoGarantia:
            dataFromBackend?.pagoInicial?.depositoGarantia ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.rentaAnticipada ??
            "",
          totalSinIva:
            dataFromBackend?.pagoInicial?.totalSinIva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.subWithoutIva ??
            "",
          iva:
            dataFromBackend?.pagoInicial?.iva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.subWitIva ??
            "",
          totalConIva:
            dataFromBackend?.pagoInicial?.totalConIva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.totalWitIva ??
            "",
        },
        renta: {
          sinIva:
            dataFromBackend?.renta?.sinIva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.rentaTotal ??
            "",
          iva:
            dataFromBackend?.renta?.iva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.iva ??
            "",
          totalConIva:
            dataFromBackend?.renta?.totalConIva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.totalConIva ??
            "",
          numeroPagos:
            dataFromBackend?.renta?.numeroPagos ??
            dataFromBackend?.pagoInicial?.months ??
            "",
        },
        auto: {
          marca: dataFromBackend?.auto?.marca ?? persona?.vehicle?.brand ?? "",
          modelo:
            dataFromBackend?.auto?.modelo ?? persona?.vehicle?.model ?? "",
          version:
            dataFromBackend?.auto?.version ??
            persona?.vehicle?.description ??
            "",
          tipoMotor: dataFromBackend?.auto?.tipoMotor ?? persona?.engine ?? "",
          tipoAuto:
            dataFromBackend?.auto?.tipoAuto ?? persona?.vehicule_type ?? "",
          accesoriosConIva:
            dataFromBackend?.auto?.accesoriosConIva ??
            persona?.financial_details?.groupConcepts
              ?.servicioEntregaSeguimiento ??
            "",
          estadoPlaca:
            dataFromBackend?.auto?.estadoPlaca ?? persona?.state_plate_id ?? "",
          agencia: dataFromBackend?.auto?.agencia ?? "",
          numeroSerie: dataFromBackend?.auto?.numeroSerie ?? "",
          motor: dataFromBackend?.auto?.motor ?? "",
          valorFactura:
            dataFromBackend?.auto?.valorFactura ?? persona?.price ?? "",
          satProductService:
            dataFromBackend?.auto?.satProductService ?? "",

          satUnit:
            dataFromBackend?.auto?.satUnit ?? "",
          serialNumber:
            dataFromBackend?.auto?.serialNumber ?? "",
        },
      };
      return objectPF;
    } else {
      const objectPM = {
        tenant: {
          nombreArrendamiento:
            dataFromBackend?.tenant?.nombreArrendamiento ?? persona?.name ?? "",
          numeroContrato:
            dataFromBackend?.tenant?.numeroContrato != null
              ? String(dataFromBackend?.tenant?.numeroContrato)
              : "",
          domicilioOperativo: dataFromBackend?.tenant?.domicilioOperativo ?? "",
          domicilioFiscal: dataFromBackend?.tenant?.domicilioFiscal ?? "",
          rfc: dataFromBackend?.tenant?.rfc ?? "",
          telefono: dataFromBackend?.tenant?.telefono ?? "",
          correo: dataFromBackend?.tenant?.correo ?? "",
          fechaRegistro:
            parseDateString(dataFromBackend?.tenant?.fechaRegistro) ?? null,
          actividadEconomica: dataFromBackend?.tenant?.actividadEconomica ?? "",
          fechaInscripcion:
            parseDateString(dataFromBackend?.tenant?.fechaInscripcion) ?? null,
          fechaConstitucion:
            parseDateString(dataFromBackend?.tenant?.fechaConstitucion) ?? null,
          numeroEscritura: dataFromBackend?.tenant?.numeroEscritura ?? "",
          libro: dataFromBackend?.tenant?.libro ?? "",
          fedatarioNumero: dataFromBackend?.tenant?.fedatarioNumero ?? "",
          entidadFederativa:
            dataFromBackend?.tenant?.entidadFederativa ??
            persona?.state_plate_id ??
            "",
          fedatarioNombre: dataFromBackend?.tenant?.fedatarioNombre ?? "",
          opinionCumplimiento:
            dataFromBackend?.tenant?.opinionCumplimiento ?? "",
          estatus: dataFromBackend?.tenant?.estatus ?? "",
          idCIF: dataFromBackend?.tenant?.idCIF ?? "",
          regimenFiscal:
            dataFromBackend?.tenant?.regimenFiscal ?? persona?.regimen_id ?? "",
        },
        equity: (() => {
          // Tomar todos los datos del backend
          const backendEquity = dataFromBackend?.equity || [];
          const equityArray = [...backendEquity];

          // Si hay menos de 3 elementos, completar con objetos vacíos
          while (equityArray.length < 3) {
            equityArray.push({
              accionista: "",
              valor: "",
              porcentaje: "",
            });
          }

          return equityArray;
        })(),
        legalRep: {
          nombre: dataFromBackend?.legalRep?.nombre ?? "",
          fechaNacimiento:
            parseDateString(dataFromBackend?.legalRep?.fechaNacimiento) ?? null,
          entidadNacimiento: dataFromBackend?.legalRep?.entidadNacimiento ?? "",
          identificacion: dataFromBackend?.legalRep?.identificacion ?? "",
          claveElector: dataFromBackend?.legalRep?.claveElector ?? "",
          emision: parseDateString(dataFromBackend?.legalRep?.emision) ?? null,
          vigencia:
            parseDateString(dataFromBackend?.legalRep?.vigencia) ?? null,
          ocr: dataFromBackend?.legalRep?.ocr ?? "",
          vencimiento:
            parseDateString(dataFromBackend?.legalRep?.vencimiento) ?? null,
          nss: dataFromBackend?.legalRep?.nss ?? "",
        },
        guarantor: {
          nombre: dataFromBackend?.guarantor?.nombre ?? "",
          fechaNacimiento:
            parseDateString(dataFromBackend?.guarantor?.fechaNacimiento) ??
            null,
          curp: dataFromBackend?.guarantor?.curp ?? "",
          direccion: dataFromBackend?.guarantor?.direccion ?? "",
          rfc: dataFromBackend?.guarantor?.rfc ?? "",
          identificacion: dataFromBackend?.guarantor?.identificacion ?? "",
          claveElector: dataFromBackend?.guarantor?.claveElector ?? "",
          emision: parseDateString(dataFromBackend?.guarantor?.emision) ?? null,
          vigencia:
            parseDateString(dataFromBackend?.guarantor?.vigencia) ?? null,
          nss: dataFromBackend?.guarantor?.nss ?? "",
        },
        contrato: {
          vigenciaMeses:
            dataFromBackend?.contrato?.vigenciaMeses ??
            dataFromBackend?.pagoInicial?.months ??
            "",
          fechaPrimerPago:
            parseDateString(dataFromBackend?.contrato?.fechaPrimerPago) ?? null,
          tasaMoratoriaDiaria:
            dataFromBackend?.contrato?.tasaMoratoriaDiaria ?? "",
        },
        pagoInicial: {
          enganche:
            dataFromBackend?.pagoInicial?.enganche ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.enganche ??
            "",
          comisionApertura:
            dataFromBackend?.pagoInicial?.comisionApertura ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.comisionApertura ??
            "",
          recuperacionGastos:
            dataFromBackend?.pagoInicial?.recuperacionGastos ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.gastosOriginacion ??
            "",
          depositoGarantia:
            dataFromBackend?.pagoInicial?.depositoGarantia ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.rentaAnticipada ??
            "",
          totalSinIva:
            dataFromBackend?.pagoInicial?.totalSinIva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche
              ?.subWithoutIva ??
            "",
          iva:
            dataFromBackend?.pagoInicial?.iva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.subWitIva ??
            "",
          totalConIva:
            dataFromBackend?.pagoInicial?.totalConIva ??
            dataFromBackend?.pagoInicial?.groupConceptsEnganche?.totalWitIva ??
            "",
        },
        renta: {
          sinIva:
            dataFromBackend?.renta?.sinIva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.rentaTotal ??
            "",
          iva:
            dataFromBackend?.renta?.iva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.iva ??
            "",
          totalConIva:
            dataFromBackend?.renta?.totalConIva ??
            dataFromBackend?.pagoInicial?.groupConcepts?.totalConIva ??
            "",
          numeroPagos:
            dataFromBackend?.renta?.numeroPagos ??
            dataFromBackend?.pagoInicial?.months ??
            "",
        },
        auto: {
          marca: dataFromBackend?.auto?.marca ?? persona?.vehicle?.brand ?? "",
          modelo:
            dataFromBackend?.auto?.modelo ?? persona?.vehicle?.model ?? "",
          version:
            dataFromBackend?.auto?.version ??
            persona?.vehicle?.description ??
            "",
          tipoMotor: dataFromBackend?.auto?.tipoMotor ?? persona?.engine ?? "",
          tipoAuto:
            dataFromBackend?.auto?.tipoAuto ?? persona?.vehicule_type ?? "",
          accesoriosConIva:
            dataFromBackend?.auto?.accesoriosConIva ??
            persona?.financial_details?.groupConcepts
              ?.servicioEntregaSeguimiento ??
            "",
          estadoPlaca:
            dataFromBackend?.auto?.estadoPlaca ?? persona?.state_plate_id ?? "",
          agencia: dataFromBackend?.auto?.agencia ?? "",
          numeroSerie: dataFromBackend?.auto?.numeroSerie ?? "",
          motor: dataFromBackend?.auto?.motor ?? "",
          valorFactura:
            dataFromBackend?.auto?.valorFactura ?? persona?.price ?? "",
          satProductService:
            dataFromBackend?.auto?.satProductService ?? "",

          satUnit:
            dataFromBackend?.auto?.satUnit ?? "",
          serialNumber:
            dataFromBackend?.auto?.serialNumber ?? "",
        },
      };
      return objectPM;
    }
  };

  const methods = useForm({
    defaultValues: useMemo(() => buildDefaultValues(), [persona?.id]),
  });

  // Obtener dirtyFields de manera optimizada
  const {
    formState: { dirtyFields },
  } = methods;

  // Cargar datos del backend al abrir el diálogo o cambiar persona (sin caché)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // ✅ PRIMERO: Resetear el formulario a valores limpios/vacíos antes de cargar datos
        const cleanDefaults = buildDefaultValues();
        methods.reset(cleanDefaults, { keepDirty: false, keepErrors: false });
        setSaveStatus("idle");
        setActiveIndex(0);

        if (persona.id && lastFetchedPersonaIdRef.current !== persona.id) {
          setLoading(true);
          let typePerson = persona.tipo_persona;
          const apiData = await fetchPreloanForm(persona.id, typePerson);
          setLoading(false);

          if (apiData.completed) {
            setCompletedSteps(apiData.completed);
          }

          if (apiData.finalizado) {
            setSaveStatus("finalized");
            onContractFinalized?.(true);
            /*  setActiveIndex((prev) => Math.min(stepItems.length - 1, prev + 1)); */
          } else {
            onContractFinalized?.(false);
          }
          const nextDefaults = buildDefaultValues(apiData);
          methods.reset(nextDefaults, { keepDirty: false, keepErrors: false });
          lastSavedValuesRef.current = nextDefaults;
          lastFetchedPersonaIdRef.current = persona.id; // Marcar como cargado
        } else if (persona.id) {
          // Si es la misma persona, aún así resetear con datos limpios
          lastSavedValuesRef.current = cleanDefaults;
        }
      } catch (error) {
        setLoading(false);

        // si falla, usar defaults mínimos
        const fallback = buildDefaultValues();
        methods.reset(fallback, { keepDirty: false, keepErrors: false });
        lastSavedValuesRef.current = fallback;
        // No marcamos como cargado si falló, para permitir reintento
      }
    };

    if (visible) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.id, visible]);

  // Resetear el flag de carga cuando cambie la persona
  useEffect(() => {
    if (persona?.id && lastFetchedPersonaIdRef.current !== persona.id) {
      lastFetchedPersonaIdRef.current = null; // Permitir nueva carga
    }
  }, [persona?.id]);
  // Autosave with debounce (2s) and diff (opcional)
  useEffect(() => {
    if (!enableAutosave) return;
    const subscription = methods.watch(() => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(async () => {
        if (abortRef.current) {
          abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;
        setSaveStatus("saving");
        try {
          const current = methods.getValues();
          const last = lastSavedValuesRef.current || {};
          const diff = computeDiff(last, current);
          if (Object.keys(diff).length > 0) {
            /*     await savePreloanForm(persona.id, diff, { signal: controller.signal }); */
            lastSavedValuesRef.current = current;
          }
          setSaveStatus("saved");
        } catch (e) {
          if (e?.name === "CanceledError") return;
          setSaveStatus("error");
        }
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.id, enableAutosave]);

  // Ensure activeIndex is within bounds when stepItems changes
  useEffect(() => {
    if (activeIndex >= stepItems.length) {
      setActiveIndex(0);
    }
  }, [stepItems.length, activeIndex]);

  const computeDiff = (prevObj, nextObj) => {
    try {
      const result = {};
      const walk = (p, n, path, out) => {
        if (typeof n !== "object" || n === null) {
          if (p !== n) out[path] = n;
          return;
        }
        if (Array.isArray(n)) {
          const max = Math.max(Array.isArray(p) ? p.length : 0, n.length);
          for (let i = 0; i < max; i++) {
            walk(p?.[i], n[i], `${path}[${i}]`, out);
          }
          return;
        }
        const keys = new Set([...(p ? Object.keys(p) : []), ...Object.keys(n)]);
        for (const k of keys) {
          const nextPath = path ? `${path}.${k}` : k;
          walk(p ? p[k] : undefined, n[k], nextPath, out);
        }
      };
      walk(prevObj, nextObj, "", result);
      // Clean leading dots from paths
      const cleaned = {};
      Object.keys(result).forEach((k) => {
        const ck = k.startsWith(".") ? k.slice(1) : k;
        cleaned[ck] = result[k];
      });
      return cleaned;
    } catch {
      return nextObj || {};
    }
  };

  const onHide = () => setVisible(false);

  const handleNext = async (e, stepKey) => {
    try {
      // Validar el paso actual antes de proceder
      let isValid;

      // Determinar la clave correcta para REQUIRED_FIELDS
      let requiredFieldsKey = stepKey;
      if (stepKey === "tenant" && persona.tipo_persona === "PM") {
        requiredFieldsKey = "tenantPM";
      }

      const requiredFields = REQUIRED_FIELDS[requiredFieldsKey] || [];

      if (requiredFields.length > 0) {
        // 1. Validar reglas de formato existentes
        const triggerValid = await methods.trigger(requiredFields);

        // 2. Validar manualmente que no estén vacíos
        let manualValid = true;
        const currentValues = methods.getValues();

        requiredFields.forEach(fieldPath => {

          const parts = fieldPath.split('.');
          const parentKey = parts[0];
          const childKey = parts[1];
          const parentValue = currentValues[parentKey];

          if (Array.isArray(parentValue)) {
            if (parentValue.length === 0) {
              manualValid = false;
              methods.setError(parentKey, { type: 'manual', message: 'Se requiere al menos un registro' });
            }
            parentValue.forEach((item, index) => {
              const itemValue = item[childKey];
              const isEmpty = itemValue === undefined || itemValue === null || itemValue === '';
              if (isEmpty) {
                manualValid = false;
                methods.setError(`${parentKey}.${index}.${childKey}`, { type: 'manual', message: 'Este campo es requerido' });
              }
            });
          } else {
            const value = methods.getValues(fieldPath);
            const isEmpty = value === undefined || value === null || value === '';
            if (isEmpty) {
              manualValid = false;
              methods.setError(fieldPath, { type: 'manual', message: 'Este campo es requerido' });
            }
          }
        });
        isValid = triggerValid && manualValid;
      } else {
        // Fallback si no hay required fields definidos
        isValid = await methods.trigger();
      }

      if (!isValid) {
        toast.current.show({
          severity: "warn",
          summary: "Campos incompletos",
          detail: "Por favor complete todos los campos requeridos antes de continuar.",
          life: 3000,
        });
        // Enfocar el primer campo con error en este paso
        const formErrors = methods.formState.errors;

        const stepFields = Object.keys(formErrors).filter((key) =>
          key.startsWith(`${stepKey}.`)
        );

        if (stepFields.length > 0) {
          // Enfocar el primer campo con error
          const firstErrorField = stepFields[0];
          document.querySelector(`[name="${firstErrorField}"]`)?.focus();
        }
        return; // Detener si la validación falla
      }

      // cancelar request previo en curso si existe
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setSaveStatus("saving");

      const current = methods.getValues();
      const changedForStep = pickDirty(
        current?.[stepKey],
        dirtyFields?.[stepKey]
      );
      // si no hay cambios en esta sección, avanzar sin guardar
      /*  if (!changedForStep || (typeof changedForStep === 'object' && Object.keys(changedForStep).length === 0)) {
                setSaveStatus("saved");
                setActiveIndex((prev) => Math.min(stepItems.length - 1, prev + 1));
                return;
            }  */

      let dataToSave = {
        [stepKey]: current[stepKey],
      };

      /*  if (stepKey === "guarantor") {
         const { direccion, rfc, identificacion, ...cleanGuarantor } = current[stepKey];
         dataToSave = {
           [stepKey]: cleanGuarantor,
         };
       } */
      const dataResponse = await savePreloanForm(
        persona.id,
        dataToSave,
        stepKey,
        persona.tipo_persona,
        { signal: controller.signal }
      );
      if (dataResponse.completed) {
        setCompletedSteps(dataResponse.completed);
      }

      if (dataResponse.finalizado) {
        setSaveStatus("finalized");
        toast.current.show({
          severity: "success",
          summary: "Contrato Finalizado",
          detail: "El contrato se ha completado exitosamente.",
          life: 3000,
        });
        setActiveIndex((prev) => Math.min(stepItems.length - 1, prev + 1));
        return;
      } else {
        setSaveStatus("saved");
        // Avanzar al siguiente paso si no es el último
        /* if (activeIndex < stepItems.length - 1) {
          setActiveIndex((prev) => prev + 1);
        } */
        /* setActiveIndex((prev) => Math.min(stepItems.length - 1, prev + 1)); */
      }
      // Resetear dirty de los campos guardados (sin tocar valores)
      const fieldPaths = collectFieldPaths(changedForStep, stepKey);
      fieldPaths.forEach((name) => {
        const defVal = getValueAtPath(current, name);
        try {
          methods.resetField(name, { defaultValue: defVal });
        } catch (_) { }
      });
      lastSavedValuesRef.current = current;
      setSaveStatus("saved");
      setActiveIndex((prev) => Math.min(stepItems.length - 1, prev + 1));
    } catch (e) {
      if (e?.name === "CanceledError") return;
      toast.current.show({
        severity: "error",
        summary: "Error al guardar",
        detail: "Hubo un error al guardar.",
        life: 8000,
      });
      setSaveStatus("error");
    }
  };

  const handleFinalize = async () => {
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      setSaveStatus("saving");
      const current = methods.getValues();
      const dataToSave = {
        isFinal: true,
        current: current,
      };
      await savePreloanForm(
        persona.id,
        dataToSave,
        "finalize",
        persona.tipo_persona,
        { signal: controller.signal }
      );
      setSaveStatus("saved");
    } catch (e) {
      if (e?.name === "CanceledError") return;
      setSaveStatus("error");
    }
  };

  // Función para validar y guardar antes de navegar en el stepper
  const validateBeforeNavigate = async (currentStepIndex) => {
    if (!stepItems[currentStepIndex]) return false;
    const stepKey = stepItems[currentStepIndex].key;

    // Validar el paso actual antes de proceder
    let isValid;
    // Determinar la clave correcta para REQUIRED_FIELDS (ej: tenant vs tenantPM)
    let requiredFieldsKey = stepKey;
    if (stepKey === "tenant" && persona.tipo_persona === "PM") {
      requiredFieldsKey = "tenantPM";
    }

    const requiredFields = REQUIRED_FIELDS[requiredFieldsKey] || [];

    if (requiredFields.length > 0) {
      // 1. Validar reglas de formato existentes (email, rfc, etc)
      const triggerValid = await methods.trigger(requiredFields);

      // 2. Validar manualmente que no estén vacíos (por si el schema no tiene 'required')
      let manualValid = true;

      // Obtener valores actuales
      const currentValues = methods.getValues();

      requiredFields.forEach(fieldPath => {
        // Manejo especial para arrays (equity, referencias)
        const parts = fieldPath.split('.');
        const parentKey = parts[0];
        const childKey = parts[1];

        const parentValue = currentValues[parentKey];

        if (Array.isArray(parentValue)) {
          // Si es un array, validar que la propiedad exista en TODOS los elementos
          if (parentValue.length === 0) {
            // Si el array es requerido y está vacío, es error? 
            // Asumiremos que al menos 1 elemento es necesario si hay campos requeridos
            manualValid = false;
            methods.setError(parentKey, { type: 'manual', message: 'Se requiere al menos un registro' });
          }

          parentValue.forEach((item, index) => {
            const itemValue = item[childKey];
            const isEmpty = itemValue === undefined || itemValue === null || itemValue === '';

            if (isEmpty) {
              manualValid = false;
              // Marcar error en el campo específico del índice
              methods.setError(`${parentKey}.${index}.${childKey}`, {
                type: 'manual',
                message: 'Este campo es requerido'
              });
            }
          });
        } else {
          // Lógica estándar para objetos simples
          const value = methods.getValues(fieldPath);
          const isEmpty = value === undefined || value === null || value === '';

          if (isEmpty) {
            manualValid = false;
            methods.setError(fieldPath, {
              type: 'manual',
              message: 'Este campo es requerido'
            });
          }
        }
      });

      isValid = triggerValid && manualValid;
    } else {
      // Si no hay campos requeridos explícitos, usar la lógica anterior de recolectar todos
      const current = methods.getValues();
      const stepData = current[stepKey];
      const stepFieldPaths = collectFieldPaths(stepData, stepKey);

      if (stepFieldPaths.length > 0) {
        isValid = await methods.trigger(stepFieldPaths);
      } else {
        isValid = true;
      }
    }
    if (!isValid) {
      // Mostrar mensaje de error
      toast.current.show({
        severity: "warn",
        summary: "Campos incompletos",
        detail:
          "Por favor complete todos los campos requeridos antes de continuar.",
        life: 3000,
      });

      // Enfocar el primer campo con error en este paso
      const formErrors = methods.formState.errors;
      const stepFields = Object.keys(formErrors).filter((key) =>
        key.startsWith(`${stepKey}.`)
      );

      if (stepFields.length > 0) {
        // Enfocar el primer campo con error
        const firstErrorField = stepFields[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      return false;
    }

    // ✅ Si la validación es exitosa, guardar los datos
    try {
      // Cancelar request previo en curso si existe
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setSaveStatus("saving");

      const current = methods.getValues();
      const changedForStep = pickDirty(
        current?.[stepKey],
        dirtyFields?.[stepKey]
      );

      let dataToSave = {
        [stepKey]: current[stepKey],
      };

      const dataResponse = await savePreloanForm(
        persona.id,
        dataToSave,
        stepKey,
        persona.tipo_persona,
        { signal: controller.signal }
      );

      if (dataResponse.finalizado) {
        toast.current.show({
          severity: "success",
          summary: "Pre préstamo guardado",
          detail: "El pre préstamo se guardó correctamente.",
          life: 8000,
        });
        setSaveStatus("finalized");
        return true;
      }

      // Resetear dirty de los campos guardados (sin tocar valores)
      const fieldPaths = collectFieldPaths(changedForStep, stepKey);
      fieldPaths.forEach((name) => {
        const defVal = getValueAtPath(current, name);
        try {
          methods.resetField(name, { defaultValue: defVal });
        } catch (_) { }
      });
      lastSavedValuesRef.current = current;
      setSaveStatus("saved");

      return true;
    } catch (e) {
      if (e?.name === "CanceledError") return false;
      toast.current.show({
        severity: "error",
        summary: "Error al guardar",
        detail: "Hubo un error al guardar.",
        life: 8000,
      });
      setSaveStatus("error");
      return false;
    }
  };

  const dialogHeader = (
    <div className="preloan-dialog-header-content">
      <h2 className="text-xl font-bold">
        Pre-Contrato - Vaciado de Información
      </h2>
      <Button
        variant="ghost"
        size="icon"
        onClick={onHide}
        className="preloan-close-button"
        icon="pi pi-times"
      />
    </div>
  );

  const dialogFooter = (
    <div className="preloan-dialog-footer">
      <Button
        label="Anterior"
        variant="outline"
        onClick={() => setActiveIndex((prev) => Math.max(0, prev - 1))}
        disabled={activeIndex === 0}
        className="Button Blue"
        style={{ minWidth: "110px" }}
        icon="pi pi-chevron-left"
      />
      <div
        className="preloan-step-indicator w-full flex items-center justify-around"
        style={{ alignItems: "center", justifyContent: "space-around", flexWrap: "wrap" }}
      >
        <span className="preloan-step-indicator">
          Paso {activeIndex + 1} de {stepItems.length}
        </span>
        {saveStatus === "saving" && (
          <span className="text-sm" style={{ color: "#0891b2" }}>
            Guardando…
          </span>
        )}
        {saveStatus === "saved" && (
          <span className="text-sm" style={{ color: "#16a34a" }}>
            Guardado
          </span>
        )}
        {saveStatus === "error" && (
          <span className="text-sm" style={{ color: "#dc2626" }}>
            Error al guardar
          </span>
        )}
        {saveStatus === "finalized" && (
          <span className="text-sm" style={{ color: "#16a34a" }}>
            Contrato finalizado
          </span>
        )}
      </div>
      {/*      {activeIndex < stepItems.length - 1 ? ( */}
      <Button
        label={
          activeIndex < stepItems.length - 1
            ? "Siguiente"
            : saveStatus === "finalized"
              ? "Descargar PDF"
              : "Finalizar"
        }
        disabled={false}
        onClick={async (e) => {
          const isLast = activeIndex === stepItems.length - 1;
          // El contrato es editable post-finalize (plan §0.6): SIEMPRE pasamos por
          // handleNext para guardar. handleNext ya avanza al siguiente step tras
          // guardar; en el último step no avanza, así que ahí abrimos el PDF
          // explícitamente con los datos recién guardados.
          if (stepItems[activeIndex]) {
            await handleNext(e, stepItems[activeIndex].key);
          }
          if (isLast && saveStatus === "finalized") {
            setPdfModalVisible(true);
          }
        }}
        className={`Button ${saveStatus === "finalized" ? "Green" : "Blue"}`}
        style={{ minWidth: "110px" }}
        icon={
          saveStatus === "finalized" && activeIndex === stepItems.length - 1
            ? "pi pi-download"
            : "pi pi-chevron-right"
        }
        iconPos="right"
      />
    </div>
  );

  return (
    <div className="card flex justify-content-center">
      <style>{styles}</style>

      <Dialog
        visible={visible}
        style={{
          width: "40vw",
          height: "60vh",
          minWidth: getMinWidth(),
          minHeight: "600px",
        }}
        onHide={onHide}
        header={dialogHeader}
        footer={dialogFooter}
        closable={false}
        modal={false}
        className="preloan-dialog"
      >
        {(saveStatus === "saving" || loading) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 2000,
              backgroundColor: "rgba(255,255,255,0.4)",
            }}
          >
            <Loader msg={loading ? "Cargando información..." : "Guardando..."} />
          </div>
        )}
        {/* Stepper */}
        <div className="preloan-stepper-container">
          <CustomStepper
            steps={stepItems}
            activeIndex={activeIndex}
            onStepClick={setActiveIndex}
            validateBeforeNavigate={validateBeforeNavigate}
            saveStatus={saveStatus}
            completedSteps={completedSteps}
            handleNext={handleNext}
          />
        </div>

        {/* Content */}
        <FormProvider {...methods}>
          <div className="preloan-content-area">
            <ResponsiveDialogProvider>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {stepItems[activeIndex]?.label}
                </h3>
                <p className="text-sm text-gray-600">
                  Complete la información requerida. Los campos pueden venir
                  prellenados y son editables.
                </p>
              </div>
              {stepItems[activeIndex]?.key === "tenant" &&
                (persona.tipo_persona === "PF" ? (
                  <StepTenant />
                ) : (
                  <StepTenantPM />
                ))}
              {stepItems[activeIndex]?.key === "referencias" && (
                <StepReferences />
              )}
              {stepItems[activeIndex]?.key === "equity" && <StepEquity />}
              {stepItems[activeIndex]?.key === "legalRep" && <StepLegalRep />}
              {stepItems[activeIndex]?.key === "guarantor" && <StepGuarantor />}
              {stepItems[activeIndex]?.key === "contrato" && <StepContract />}
              {stepItems[activeIndex]?.key === "pagoInicial" && (
                <StepDownPayment />
              )}
              {stepItems[activeIndex]?.key === "renta" && <StepRent />}
              {stepItems[activeIndex]?.key === "auto" && <StepVehicle />}
            </ResponsiveDialogProvider>
          </div>
        </FormProvider>
      </Dialog>

      {/* PDF Modal */}
      <PDFModal
        visible={pdfModalVisible}
        onHide={() => setPdfModalVisible(false)}
        personaId={persona.id}
        type={persona.tipo_persona}
      />
    </div>
  );
}
